import { useEffect, useState } from 'react'

import DataObjectIcon from '@mui/icons-material/DataObject'
import { CircularProgress, IconButton, Stack, Typography } from '@mui/material'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { CopyContent } from '@/components/CopyContent'
import { golfPricingApi } from '@/service/golfPricingApi'

import { OddsDataRow } from './oddsData.dde.table'

type RowJsonDialogProps = {
  row: OddsDataRow
}

export const RowJsonDialog = ({ row }: RowJsonDialogProps) => {
  const [open, setOpen] = useState(false)
  const [jsonString, setJsonString] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return

    if (row.actionType !== 'Odds Update' && row.rawActionData) {
      setJsonString(JSON.stringify(row.rawActionData, null, 2))
      setLoading(false)
      setError(null)
      return
    }

    if (row.dlSourceKey) {
      const fetchMessage = async () => {
        setLoading(true)
        setError(null)
        try {
          const { data } = await golfPricingApi.endpoints.odds.getMessage({
            messageKey: row.dlSourceKey
          })
          setJsonString(JSON.stringify(data, null, 2))
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load message')
          setJsonString('')
        } finally {
          setLoading(false)
        }
      }

      fetchMessage()
    } else {
      setError('No data available')
      setLoading(false)
    }
  }, [open, row.dlSourceKey, row.actionType, row.rawActionData])

  return (
    <>
      <IconButton onClick={() => setOpen(true)} size="small">
        <DataObjectIcon />
      </IconButton>
      <ConsoleDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Action JSON"
        dialogSize="md"
        disableActions
        hideCloseAction={false}
        CloseButtonProps={{ text: 'Close' }}
      >
        {loading || error ? (
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            sx={{
              minHeight: 200,
              py: 4
            }}
          >
            {loading && <CircularProgress />}
            <Typography variant="body2" color={loading ? 'text.secondary' : 'error'}>
              {loading ? 'Loading JSON...' : `Error: ${error}`}
            </Typography>
          </Stack>
        ) : (
          <CopyContent value={jsonString} mono label="Action JSON" />
        )}
      </ConsoleDialog>
    </>
  )
}
